import {
  LayoutDashboard as DashboardIcon,
  PiggyBank as SavingsIcon,
  Users as GroupIcon,
  ShieldCheck as SecurityIcon,
  TrendingUp as TrendingUpIcon,
  Bot as SmartIcon,
  Gauge as SpeedIcon,
  Landmark as AccountIcon,
  CreditCard as CardIcon,
  BarChart3 as TimelineIcon,
  Target,
  Clock,
  CheckCircle,
  Zap,
  UserPlus,
  Wallet,
} from "lucide-react";

export const iconBase = { size: 18, strokeWidth: 2.2 };

export const ajoModules = [
  {
    id: "overview",
    name: "Thrift Overview",
    icon: DashboardIcon,
    color: "#6C63FF",
    description: "Your Ajo thrift dashboard at a glance.",
  },
  {
    id: "savings",
    name: "Contributions",
    icon: SavingsIcon,
    color: "#10B981",
    description: "Track every contribution, balance, and payout in real time.",
  },
  {
    id: "groups",
    name: "Group Cycles",
    icon: GroupIcon,
    color: "#3B82F6",
    description: "Manage thrift circles, member roles, and cycle progress.",
  },
  {
    id: "security",
    name: "Security",
    icon: SecurityIcon,
    color: "#8B5CF6",
    description: "Keep contributions, payouts, and member data protected.",
  },
  {
    id: "analytics",
    name: "Insights",
    icon: TrendingUpIcon,
    color: "#F59E0B",
    description: "See contribution trends, cycle performance, and growth.",
  },
];

export const leftPanelContent = {
  overview: {
    title: "Your Group's Financial Health at a Glance",
    description:
      "Get immediate visibility into active circles, total group savings, upcoming payout dates, and individual contribution status.",
    features: [
      {
        number: "01",
        title: "Thrift Ready",
        description:
          "Ajo gives you a clear view of every thrift cycle, contribution, and payout so the group stays aligned.",
        icon: SmartIcon,
      },
      {
        number: "02",
        title: "Simple for Every Member",
        description:
          "Designed for thrift circles, Ajo makes it easy for organizers and members to follow the cycle without confusion.",
        icon: SpeedIcon,
      },
      {
        number: "03",
        title: "Protected Contributions",
        description:
          "Ajo protects member details, cycle records, and contribution history with robust security controls.",
        icon: SecurityIcon,
      },
      {
        number: "04",
        title: "Built for Growth",
        description:
          "Backed by reliable technology, Ajo helps thrift groups scale from small circles to larger contribution communities.",
        icon: TrendingUpIcon,
      },
    ],
  },
  savings: {
    title: "Automated Payments, Zero Hassle",
    description:
      "Schedule recurring deposits, send automated reminders to members, and track payment history with complete transparency.",
    features: [
      {
        number: "01",
        title: "Contribution Monitoring",
        description:
          "Set and track cycle targets with clear progress bars and reminders that keep the group on schedule.",
        icon: Target,
      },
      {
        number: "02",
        title: "Cycle Planning",
        description:
          "Plan contribution dates with automated schedules and reminders so every member knows when to pay in.",
        icon: Clock,
      },
      {
        number: "03",
        title: "Wallet Tracking",
        description:
          "See how each debit, credit, and wallet movement affects the group pot and your balance.",
        icon: Wallet,
      },
      {
        number: "04",
        title: "Payout Returns",
        description:
          "Watch expected payouts, cycle returns, and earned value update as the thrift progresses.",
        icon: TrendingUpIcon,
      },
    ],
  },
  groups: {
    title: "Seamless Rotation & Payout Tracking",
    description:
      "Manage payout order, track active cycle progress, and ensure every member gets funded on time, every time.",
    features: [
      {
        number: "01",
        title: "Regular Contribution Cycle",
        description:
          "Set recurring contribution cycles with automated reminders for groups that prefer structure and predictability.",
        icon: Clock,
      },
      {
        number: "02",
        title: "Flexible Group Thrift",
        description:
          "Contribute at your own pace toward a shared target without a rigid auto-debit schedule.",
        icon: Zap,
      },
      {
        number: "03",
        title: "Start a Thrift Circle",
        description:
          "Create a group, invite trusted friends or family, and manage all contributions and payouts in one transparent place.",
        icon: UserPlus,
      },
      {
        number: "04",
        title: "Rotate & Complete Payouts",
        description:
          "Members pay in, and one person collects each cycle. Everyone gets their turn, and everyone wins.",
        icon: CheckCircle,
      },
    ],
  },
  security: {
    title: "Built-In Protection for Every Circle",
    description:
      "Bank-grade encryption, 2FA, and continuous account monitoring ensure your group funds and member identity remain 100% secure.",
    features: [
      {
        number: "01",
        title: "Advanced Encryption",
        description:
          "All data and transactions are protected with bank-grade AES-256 encryption, ensuring your information stays confidential.",
        icon: SecurityIcon,
      },
      {
        number: "02",
        title: "Two-Factor Authentication",
        description:
          "Add an extra layer of security to your account with 2FA, protecting you from unauthorized access even if your password is compromised.",
        icon: SecurityIcon,
      },
      {
        number: "03",
        title: "Real-Time Monitoring",
        description:
          "Continuous monitoring of account activity helps detect and prevent fraudulent transactions before they impact the group.",
        icon: TimelineIcon,
      },
      {
        number: "04",
        title: "Data Privacy",
        description:
          "Your personal information is handled with the strictest privacy standards and is never shared without your explicit consent.",
        icon: SecurityIcon,
      },
    ],
  },
  analytics: {
    title: "Data-Driven Decisions for Better Saving",
    description:
      "Analyze savings growth, monitor member consistency, and gain actionable insights to optimize future savings goals.",
    features: [
      {
        number: "01",
        title: "Growth Rate Analysis",
        description:
          "Track month-over-month thrift growth with interactive charts that highlight trends and forecast future potential.",
        icon: TrendingUpIcon,
      },
      {
        number: "02",
        title: "Group Performance",
        description:
          "Visualize contribution patterns and performance across your thrift groups to ensure everyone stays on track.",
        icon: GroupIcon,
      },
      {
        number: "03",
        title: "Contribution & Payout Breakdown",
        description:
          "Gain clarity on your cash flow with detailed breakdowns of contributions, payouts, and net group balance over time.",
        icon: TimelineIcon,
      },
      {
        number: "04",
        title: "Cycle Progress Tracking",
        description:
          "Monitor the progress of each cycle, celebrate milestones, and adjust strategies to stay ahead.",
        icon: Target,
      },
    ],
  },
};

export const previewData = {
  overview: {
    title: "Ajo Thrift Dashboard",
    kpis: [
      { label: "Total Savings", value: "₦2.4M", sub: "+18% this month", color: "#6C63FF", icon: SavingsIcon },
      { label: "Active Groups", value: "12", sub: "+3 this quarter", color: "#3B82F6", icon: GroupIcon },
      { label: "Growth Rate", value: "24%", sub: "+6% vs last month", color: "#10B981", icon: TrendingUpIcon },
      { label: "Members", value: "156", sub: "+12 this month", color: "#8B5CF6", icon: AccountIcon },
    ],
    chart: "line",
    chartLabels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    chartData: [1.2, 1.5, 1.8, 2.0, 2.2, 2.4],
    chartColor: "#6C63FF",
    rows: [
      { icon: "savings", text: "New savings goal created: 'Home Project'", meta: "2 min ago" },
      { icon: "groups", text: "Group 'Tech Savers' reached ₦500k", meta: "15 min ago" },
      { icon: "security", text: "Security check completed - All clear", meta: "1 hr ago" },
      { icon: "analytics", text: "Monthly savings report generated", meta: "2 hrs ago" },
    ],
  },
  savings: {
    title: "Your Contributions",
    kpis: [
      { label: "Total Saved", value: "₦850k", sub: "+12% vs last month", color: "#10B981", icon: SavingsIcon },
      { label: "Active Goals", value: "4", sub: "2 completed this month", color: "#3B82F6", icon: TimelineIcon },
      { label: "Monthly Deposit", value: "₦120k", sub: "on track", color: "#6C63FF", icon: CardIcon },
      { label: "Interest Earned", value: "₦42k", sub: "+8% vs last month", color: "#F59E0B", icon: TrendingUpIcon },
    ],
    chart: "bar",
    chartLabels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    chartData: [50, 65, 80, 95, 110, 120],
    chartColor: "#10B981",
    rows: [
      { icon: "savings", text: "Savings goal 'Vacation' reached 75%", meta: "10 min ago" },
      { icon: "savings", text: "Monthly deposit of ₦120k made", meta: "1 hr ago" },
      { icon: "savings", text: "Interest of ₦3,200 credited", meta: "3 hrs ago" },
    ],
  },
  groups: {
    title: "Thrift Groups",
    kpis: [
      { label: "Active Groups", value: "12", sub: "3 new this month", color: "#3B82F6", icon: GroupIcon },
      { label: "Total Members", value: "156", sub: "+12 this month", color: "#8B5CF6", icon: AccountIcon },
      { label: "Group Savings", value: "₦1.2M", sub: "+8% vs last month", color: "#10B981", icon: SavingsIcon },
      { label: "Pending Invites", value: "5", sub: "2 awaiting response", color: "#F59E0B", icon: CardIcon },
    ],
    chart: "doughnut",
    chartLabels: ["Family Group", "Work Group", "Community", "Friends"],
    chartData: [35, 28, 22, 15],
    chartColor: "#3B82F6",
    rows: [
      { icon: "groups", text: "New member joined 'Work Group'", meta: "5 min ago" },
      { icon: "groups", text: "Group 'Community' reached ₦1M", meta: "30 min ago" },
      { icon: "groups", text: "Invitation sent to 2 new members", meta: "2 hrs ago" },
    ],
  },
  security: {
    title: "Trust Center",
    kpis: [
      { label: "Security Score", value: "96%", sub: "Excellent", color: "#10B981", icon: SecurityIcon },
      { label: "Last Login", value: "2 min ago", sub: "Lagos, NG", color: "#3B82F6", icon: AccountIcon },
      { label: "Active Sessions", value: "1", sub: "All devices secure", color: "#6C63FF", icon: CardIcon },
      { label: "2FA Status", value: "Enabled", sub: "Your account is protected", color: "#8B5CF6", icon: SecurityIcon },
    ],
    chart: "line",
    chartLabels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    chartData: [92, 93, 95, 94, 96, 95, 96],
    chartColor: "#8B5CF6",
    rows: [
      { icon: "security", text: "Security check completed - No issues", meta: "1 min ago" },
      { icon: "security", text: "2FA verification successful", meta: "15 min ago" },
      { icon: "security", text: "Login from new device detected - Verified", meta: "2 hrs ago" },
    ],
  },
  analytics: {
    title: "Thrift Analytics",
    kpis: [
      { label: "Growth Rate", value: "24%", sub: "+6% vs last month", color: "#10B981", icon: TrendingUpIcon },
      { label: "Avg. Deposit", value: "₦95k", sub: "+8% vs last month", color: "#3B82F6", icon: CardIcon },
      { label: "Active Savers", value: "89%", sub: "of total members", color: "#6C63FF", icon: AccountIcon },
      { label: "Projected Annual", value: "₦4.8M", sub: "on current trend", color: "#F59E0B", icon: SavingsIcon },
    ],
    chart: "bar",
    chartLabels: ["Q1", "Q2", "Q3", "Q4"],
    chartData: [850, 1200, 1600, 2100],
    chartColor: "#F59E0B",
    rows: [
      { icon: "analytics", text: "Weekly savings report generated", meta: "5 min ago" },
      { icon: "analytics", text: "Top saver: Chidi O. - ₦850k", meta: "30 min ago" },
      { icon: "analytics", text: "New milestone: 24% growth", meta: "1 hr ago" },
    ],
  },
};

export const rowIcons = {
  savings: SavingsIcon,
  groups: GroupIcon,
  security: SecurityIcon,
  analytics: TrendingUpIcon,
};

export { DashboardIcon, SavingsIcon };
